import React, { useEffect, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiCamera,
  FiCheck,
  FiMic,
  FiMonitor,
  FiRefreshCw,
  FiShield,
  FiUserCheck,
} from 'react-icons/fi';
import { useMedia } from '../context/MediaContext';
import { verifyRejoinPhoto } from '../../ai-interview/utils/aiInterviewApi';
import { insecureMediaMessage } from '../../../utils/mediaSupport';
import InterviewAudioCheck from '../../ai-interview/components/InterviewAudioCheck';

const hasLiveTrack = (stream, kind) => Boolean(
  stream?.getTracks().some((track) => track.kind === kind && track.readyState === 'live'),
);

const waitForVideo = async (video, stream) => {
  if (!video || !stream) throw new Error('Camera stream is unavailable.');
  video.srcObject = stream;
  await video.play().catch(() => {});
  if (video.readyState >= 2 && video.videoWidth > 0) return;

  await new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('The camera preview did not become ready. Restart the camera and try again.'));
    }, 5000);
    const onReady = () => {
      cleanup();
      resolve();
    };
    const cleanup = () => {
      clearTimeout(timeout);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
    };
    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });
  });
};

const StatusIcon = ({ complete }) => (
  <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${complete
    ? 'bg-emerald-500 text-white'
    : 'border border-slate-300 bg-white text-slate-400 dark:border-slate-600 dark:bg-slate-800'}`}>
    {complete ? <FiCheck size={14} /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
  </span>
);

const CheckRow = ({ icon: Icon, title, detail, complete, action }) => (
  <div className="grid min-h-[72px] grid-cols-[24px_19px_minmax(0,1fr)] items-center gap-x-3 border-b border-slate-200 px-4 py-3 last:border-b-0 dark:border-slate-700 sm:grid-cols-[24px_19px_minmax(0,1fr)_auto]">
    <StatusIcon complete={complete} />
    <Icon className="shrink-0 text-slate-500 dark:text-slate-400" size={19} />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{detail}</p>
    </div>
    {action && <div className="col-start-3 row-start-2 mt-2 sm:col-start-4 sm:row-start-1 sm:mt-0">{action}</div>}
  </div>
);

const ActionButton = ({ icon: Icon, children, onClick, disabled, secondary = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex min-h-9 shrink-0 items-center justify-center gap-2 rounded-lg px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${secondary
      ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
      : 'bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-violet-600 dark:hover:bg-violet-500'}`}
  >
    <Icon size={15} />
    {children}
  </button>
);

const MediaReadyGate = ({ requirements = {}, record = {}, rejoin = false, voiceVerified = false, ids, onReady }) => {
  const { camera, screen, capture, startMic } = useMedia();
  // Requirements are driven PURELY by the interview config — not forced on rejoin. A mic-only interview
  // (e.g. Job Seeker mock) therefore never starts the camera/screen on rejoin, and an HR interview with
  // screen disabled isn't forced to re-share. The identity photo is a camera re-check, so it only
  // applies on rejoin when the camera is actually required.
  const cameraRequired = requirements.camera_required !== false;
  // The admin enabled camera RECORDING → the camera must be live for the recorder to capture it, even if
  // the camera isn't "required" for the interview. This only STARTS the camera (best-effort recording);
  // it never GATES the interview (only `cameraRequired` does, via camOk below).
  const recordCamera = record.camera === true;
  const micRequired = requirements.mic_required !== false;
  const screenRequired = requirements.screen_required !== false;
  const photoRequired = rejoin && cameraRequired;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const firedRef = useRef(false);
  const [photoDone, setPhotoDone] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState('');
  const [audioTestSkipped, setAudioTestSkipped] = useState(false);
  const [audioWarning, setAudioWarning] = useState(false);

  useEffect(() => {
    // Start the camera when it's required for the interview OR when camera recording is enabled — the
    // recorder (MediaContext) can only capture a LIVE stream, so record.camera without a live camera
    // silently produced no file. Recording start is best-effort (never blocks; camOk gates on required).
    if ((cameraRequired || recordCamera) && !hasLiveTrack(camera.stream, 'video')) {
      camera.start().catch(() => {});
    }
    if (micRequired) startMic().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!videoRef.current || !camera.stream) return;
    waitForVideo(videoRef.current, camera.stream).catch(() => {});
  }, [camera.stream]);

  const camOk = !cameraRequired || hasLiveTrack(camera.stream, 'video');
  const micOk = !micRequired || capture.active;
  const screenOk = !screenRequired || (screen.sharing && hasLiveTrack(screen.stream, 'video'));
  const photoOk = !photoRequired || photoDone;
  const ready = camOk && micOk && screenOk && photoOk;

  const restartCamera = async () => {
    setBusy('camera');
    setActionError('');
    setPhotoDone(false);
    setPhotoPreview('');
    try {
      camera.stop();
      const stream = await camera.start();
      await waitForVideo(videoRef.current, stream);
    } catch (error) {
      setActionError(error?.message || camera.error || 'Could not start the camera. Check browser permissions and try again.');
    } finally {
      setBusy('');
    }
  };

  const shareScreen = async () => {
    setBusy('screen');
    setActionError('');
    try {
      if (screen.stream || screen.sharing) screen.stop();
      const result = await screen.start();
      if (!result.ok) {
        const message = result.reason === 'not_entire_screen'
          ? 'Select Entire Screen in the browser picker, not a window or browser tab.'
          : result.reason === 'denied'
            ? 'Screen sharing was cancelled or blocked. Click Share screen and allow access.'
            : result.reason === 'insecure'
              ? (result.error || insecureMediaMessage())
              : (result.error || 'The browser could not start screen sharing. Please try again.');
        setActionError(message);
      }
    } catch (error) {
      setActionError(error?.message || 'The browser could not start screen sharing. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const capturePhoto = async () => {
    setBusy('photo');
    setActionError('');
    try {
      let stream = camera.stream;
      if (!hasLiveTrack(stream, 'video')) stream = await camera.start();
      const video = videoRef.current;
      await waitForVideo(video, stream);

      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.save();
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      context.restore();

      const blob = await new Promise((resolve, reject) => canvas.toBlob(
        (image) => (image ? resolve(image) : reject(new Error('Could not create the photo.'))),
        'image/jpeg',
        0.9,
      ));
      await verifyRejoinPhoto(ids.companyId, ids.sessionId, ids.candidateId, blob);
      setPhotoPreview(canvas.toDataURL('image/jpeg', 0.72));
      setPhotoDone(true);
    } catch (error) {
      setPhotoDone(false);
      setActionError(error?.message || 'Could not capture the verification photo. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const enter = () => {
    if (ready && !firedRef.current) {
      firedRef.current = true;
      onReady({ audioWarning });
    }
  };

  const requiredChecks = [
    cameraRequired ? camOk : null,
    micRequired ? micOk : null,
    screenRequired ? screenOk : null,
    photoRequired ? photoOk : null,
  ].filter((value) => value !== null);
  const completedCount = requiredChecks.filter(Boolean).length;
  const remainingCount = requiredChecks.length - completedCount;

  return (
    <main className="min-h-[100dvh] bg-slate-100 px-4 py-8 text-slate-900 dark:bg-slate-950 dark:text-white sm:px-6">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-indigo-600 dark:text-violet-300">
              <FiShield size={16} /> {rejoin ? 'Secure interview rejoin' : 'Interview device check'}
            </div>
            <h1 className="text-2xl font-bold">{rejoin ? 'Welcome back' : 'Get ready'}</h1>
            <p className="mt-1 max-w-xl text-sm text-slate-600 dark:text-slate-400">
              {rejoin
                ? 'Reconnect your interview devices and confirm your identity before resuming.'
                : 'Connect the required interview devices before entering.'}
            </p>
          </div>
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">{completedCount} of {requiredChecks.length} ready</p>
        </header>

        <div className={`grid overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 ${cameraRequired ? 'lg:grid-cols-[1.15fr_0.85fr]' : ''}`}>
          {cameraRequired && (
          <section className="border-b border-slate-200 p-4 dark:border-slate-700 lg:border-b-0 lg:border-r sm:p-5">
            <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-950">
              <video ref={videoRef} autoPlay playsInline muted className={`h-full w-full object-cover [transform:scaleX(-1)] ${camOk ? '' : 'hidden'}`} />
              {!camOk && (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-300">
                  <FiCamera size={32} />
                  <p className="text-sm">Camera preview unavailable</p>
                  <ActionButton icon={FiRefreshCw} onClick={restartCamera} disabled={busy === 'camera'}>
                    {busy === 'camera' ? 'Starting...' : 'Start camera'}
                  </ActionButton>
                </div>
              )}
              {camOk && <span className="absolute bottom-3 left-3 rounded bg-black/65 px-2 py-1 text-xs font-medium text-white">Live preview</span>}
              {photoPreview && (
                <img src={photoPreview} alt="Captured identity confirmation" className="absolute bottom-3 right-3 h-16 w-24 rounded border-2 border-white object-cover shadow" />
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />

            <div className="mt-4 flex items-start gap-3 rounded-lg bg-slate-100 px-3 py-3 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <FiShield className="mt-0.5 shrink-0 text-indigo-600 dark:text-violet-300" size={16} />
              {photoRequired
                ? 'Your verification photo is captured only when you press Capture photo.'
                : screenRequired
                  ? 'Your camera and screen streams remain active as you enter the interview.'
                  : 'Your camera stream remains active as you enter the interview.'}
            </div>
          </section>
          )}

          <section className="flex min-h-[430px] flex-col">
            <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-700">
              <h2 className="text-base font-semibold">{rejoin ? 'Rejoin checks' : 'Device checks'}</h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {voiceVerified && !rejoin ? 'Microphone and voice were checked during the read-aloud.' : 'Device access is required. The microphone test is optional.'}
              </p>
            </div>

            <div>
              {cameraRequired && <CheckRow
                icon={FiCamera}
                title="Camera"
                detail={camOk ? 'Camera is connected and live.' : 'Camera permission is required.'}
                complete={camOk}
                action={<ActionButton icon={FiRefreshCw} onClick={restartCamera} disabled={busy === 'camera'} secondary={camOk}>{busy === 'camera' ? 'Starting...' : camOk ? 'Restart' : 'Enable'}</ActionButton>}
              />}
              {micRequired && <CheckRow icon={FiMic} title="Microphone" detail={micOk ? 'Microphone is connected.' : 'Allow microphone access to continue.'} complete={micOk}
                action={!micOk ? <ActionButton icon={FiMic} onClick={() => startMic().catch((error) => setActionError(error?.message || 'Could not start the microphone.'))}>Enable</ActionButton> : null} />}
              {micRequired && (!voiceVerified || rejoin) && !audioTestSkipped && (
                <div className="p-4"><InterviewAudioCheck
                  disabled={!micOk}
                  onComplete={(passed) => setAudioWarning(!passed)}
                  onSkip={() => setAudioTestSkipped(true)} /></div>
              )}
              {screenRequired && <CheckRow
                icon={FiMonitor}
                title="Entire screen"
                detail={screenOk ? 'Your entire screen is being shared.' : 'Choose Entire Screen in the browser picker.'}
                complete={screenOk}
                action={<ActionButton icon={FiMonitor} onClick={shareScreen} disabled={busy === 'screen'} secondary={screenOk}>{busy === 'screen' ? 'Opening...' : screenOk ? 'Share again' : 'Share screen'}</ActionButton>}
              />}
              {photoRequired && <CheckRow
                icon={FiUserCheck}
                title="Identity photo"
                detail={photoDone ? 'Photo captured and saved.' : 'Take a current photo from the live preview.'}
                complete={photoDone}
                action={<ActionButton icon={FiCamera} onClick={capturePhoto} disabled={busy === 'photo'} secondary={photoDone}>{busy === 'photo' ? 'Saving...' : photoDone ? 'Retake' : 'Capture photo'}</ActionButton>}
              />}
            </div>

            <div className="mt-auto border-t border-slate-200 p-4 dark:border-slate-700">
              {(actionError || camera.error || capture.error) && (
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
                  <FiAlertTriangle className="mt-0.5 shrink-0" size={15} />
                  <span>{actionError || camera.error || capture.error}</span>
                </div>
              )}
              <button type="button" onClick={enter} disabled={!ready}
                className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:bg-violet-600 dark:hover:bg-violet-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400">
                {ready
                  ? (rejoin ? 'Resume interview' : 'Enter interview')
                  : `Complete ${remainingCount} remaining check${remainingCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default MediaReadyGate;
